import ClassCard from "../components/ClassCard";
import TableToolbar from "../components/tableToolbar";
import { Suspense, lazy, useMemo, useState } from "react";
import { EmptyState } from "../components/emptyState";
import { Spinner } from "../components/spinner";
import { BookOpenIcon } from "lucide-react";
import Pagination from "../components/pagination";
import { toast } from "sonner";
import { fromZonedTime } from "date-fns-tz";
import { useAuth } from "@clerk/clerk-react";
import { useSelector } from "react-redux";
import { getUserTimezone } from "../lib/timezone";

const EditClassDialog = lazy(() => import("../forms/editClass"));

// Treat API `session_date` + `session_start_time` as UTC.
// Convert to an absolute Date so formatting in any selected timezone is correct.
const SOURCE_TIMEZONE = "UTC";

const sessionTypes = [
  { value: "all", label: "All Session Types" },
  { value: "class", label: "Class" },
  { value: "event", label: "Event" },
  { value: "meeting", label: "Meeting" },
];

// Use user's preferred timezone

function parseSessionDateTimeInSourceTimezone(sessionDate, sessionTime) {
  const datePartRaw = String(sessionDate || "").trim();
  const datePart = datePartRaw.includes("T")
    ? datePartRaw.split("T")[0]
    : datePartRaw;
  const timePartRaw = String(sessionTime || "").trim();
  if (!datePart || !timePartRaw) return null;

  const [y, m, d] = datePart.split("-").map((n) => Number(n));
  if (![y, m, d].every((n) => Number.isFinite(n))) return null;

  const timeParts = timePartRaw.split(":");
  const hh = Number(timeParts[0]);
  const mm = Number(timeParts[1]);
  const ss = Number(timeParts[2] ?? 0);
  if (![hh, mm, ss].every((n) => Number.isFinite(n))) return null;

  const wallClock = new Date(y, m - 1, d, hh, mm, ss, 0);
  if (Number.isNaN(wallClock.getTime())) return null;
  return fromZonedTime(wallClock, SOURCE_TIMEZONE);
}

const ClassSchedule = ({
  classes,
  loading,
  error,
  fetchClasses,
  fetchClassDetails,
  isAdminModeOn = false,
  currentPage = 1,
  rowsPerPage = 5,
  totalPages = 1,
  totalRows = 0,
  onPageChange,
  onRowsPerPageChange,
  extraFilters = [],
  extraSorts = [],
}) => {
  const { userId } = useAuth();
  const roles = useSelector((s) => s.auth.roles) || [];
  const isAdmin = roles.includes("Admin");
  const [search, setSearch] = useState("");
  const [sessionType, setSessionType] = useState("all");
  // Use user's preferred timezone (localStorage > browser)
  const timezone = useMemo(() => getUserTimezone(), []);
  const [instructorSort, setInstructorSort] = useState("datetime_asc");
  const [editingClass, setEditingClass] = useState(null);
  const [fetchingEditDetails, setFetchingEditDetails] = useState(false);

  const getClassOwnerId = (cls) => {
    const ownerId = cls?.owner_id ?? cls?.ownerId ?? cls?.__owner_id ?? null;
    return ownerId != null && `${ownerId}`.trim() !== "" ? String(ownerId) : "";
  };

  const getClassTmId = (cls) => {
    const tmId =
      cls?.tm_id ??
      cls?.tmId ??
      cls?.assigned_tm_id ??
      cls?.assignedTmId ??
      cls?.assigned_tm ??
      cls?.assignedTm ??
      cls?.assigned_reviewer ??
      cls?.assignedReviewer ??
      cls?.technical_mentor_id ??
      cls?.technicalMentorId ??
      cls?.__tm_id ??
      null;
    return tmId != null && `${tmId}`.trim() !== "" ? String(tmId) : "";
  };

  const canManageClass = (cls) => {
    const currentUserId = String(userId || "").trim();
    if (!currentUserId) return false;

    const ownerId = getClassOwnerId(cls);
    if (ownerId && ownerId === currentUserId) return true;

    const tmId = getClassTmId(cls);
    if (tmId && tmId === currentUserId) return true;

    // Admins should only manage classes when Admin Mode is ON.
    if (isAdmin && isAdminModeOn) return true;

    return false;
  };

  // Memoize filtered classes for performance
  const filteredClasses = useMemo(() => {
    if (!classes) return [];
    return classes.filter((cls) => {
      const title = String(
        cls?.class_title ?? cls?.classTitle ?? cls?.title ?? "",
      );
      const owner =
        `${cls?.owner_first_name ?? ""} ${cls?.owner_last_name ?? ""}`.trim();
      const matchesSearch =
        search.trim() === "" ||
        title.toLowerCase().includes(search.toLowerCase()) ||
        owner.toLowerCase().includes(search.toLowerCase());
      const resolvedType = cls?.class_type ?? cls?.classType ?? cls?.type;
      const matchesType =
        sessionType === "all" ||
        String(resolvedType || "") === String(sessionType);
      return matchesSearch && matchesType;
    });
  }, [classes, search, sessionType]);

  const sortedClasses = useMemo(() => {
    const source = Array.isArray(filteredClasses) ? filteredClasses : [];
    const sortKey = String(instructorSort || "datetime_asc");
    if (!sortKey || sortKey === "none") return source;

    if (sortKey === "datetime_asc" || sortKey === "datetime_desc") {
      const decorated = source.map((cls) => {
        const start =
          parseSessionDateTimeInSourceTimezone(
            cls?.session_date,
            cls?.session_start_time,
          ) || new Date(`${cls?.session_date}T${cls?.session_start_time}`);
        const ms =
          start && !Number.isNaN(start.getTime())
            ? start.getTime()
            : Number.POSITIVE_INFINITY;
        return { cls, startMs: ms };
      });

      decorated.sort((a, b) => a.startMs - b.startMs);
      if (sortKey === "datetime_desc") decorated.reverse();
      return decorated.map((d) => d.cls);
    }

    if (sortKey === "title_asc" || sortKey === "title_desc") {
      const decorated = source.map((cls) => ({
        cls,
        title: String(cls?.class_title ?? cls?.classTitle ?? cls?.title ?? ""),
      }));
      decorated.sort((a, b) => a.title.localeCompare(b.title));
      if (sortKey === "title_desc") decorated.reverse();
      return decorated.map((d) => d.cls);
    }

    if (sortKey === "type_asc" || sortKey === "type_desc") {
      const decorated = source.map((cls) => ({
        cls,
        type: String(cls?.class_type ?? cls?.classType ?? cls?.type ?? ""),
      }));
      decorated.sort((a, b) => a.type.localeCompare(b.type));
      if (sortKey === "type_desc") decorated.reverse();
      return decorated.map((d) => d.cls);
    }

    if (sortKey === "location_asc" || sortKey === "location_desc") {
      const decorated = source.map((cls) => ({
        cls,
        location: String(
          cls?.class_location ?? cls?.classLocation ?? cls?.location ?? "",
        ),
      }));
      decorated.sort((a, b) => a.location.localeCompare(b.location));
      if (sortKey === "location_desc") decorated.reverse();
      return decorated.map((d) => d.cls);
    }

    return source;
  }, [filteredClasses, instructorSort]);

  const effectiveSorts = useMemo(() => {
    const incoming = Array.isArray(extraSorts) ? extraSorts : [];
    if (incoming.length > 0) return incoming;

    return [
      {
        label: "Sort by",
        value: instructorSort,
        options: [
          { value: "datetime_asc", label: "Date/Time (Soonest)" },
          { value: "datetime_desc", label: "Date/Time (Latest)" },
          { value: "title_asc", label: "Class Title (A-Z)" },
          { value: "title_desc", label: "Class Title (Z-A)" },
          { value: "type_asc", label: "Session Type (A-Z)" },
          { value: "type_desc", label: "Session Type (Z-A)" },
          { value: "location_asc", label: "Location (A-Z)" },
          { value: "location_desc", label: "Location (Z-A)" },
        ],
        onChange: (v) => setInstructorSort(String(v || "datetime_asc")),
        onClear: () => setInstructorSort("datetime_asc"),
      },
    ];
  }, [extraSorts, instructorSort]);

  // Pagination is handled by the backend; `classes` is already page-scoped.

  const handleDeleteClass = (cls) => {
    fetchClasses();
  };

  const handleEditClass = async (cls) => {
    try {
      if (fetchingEditDetails) return;
      setFetchingEditDetails(true);
      const classId = cls?.class_id ?? cls?.classId ?? cls?.id;
      const sessionId = cls?.session_id ?? cls?.sessionId;
      console.log("Edit clicked: computed classId", classId);
      console.log("Edit clicked: session payload keys", Object.keys(cls || {}));

      const resolvedId = classId ?? sessionId;
      if (!resolvedId) {
        toast.error("Missing identifiers — cannot fetch class details.");
        return;
      }

      if (typeof fetchClassDetails !== "function") {
        toast.error(
          "fetchClassDetails is not wired — cannot load class details.",
        );
        return;
      }

      if (!classId && sessionId) {
        console.warn(
          "No classId in upcoming-sessions payload; attempting /liveclass/{id} with session_id as a diagnostic fallback",
          sessionId,
        );
      }

      console.log("Fetching class details via /liveclass/{id}", resolvedId);
      const details = await fetchClassDetails(resolvedId, { force: true });
      console.log("Fetched class details payload:", details);

      if (!details) {
        toast.error("No class details returned from /liveclass/{id}.");
        return;
      }

      // Merge upcoming-session fields (date/time/session_id) with class details (recurrence_pattern, etc.)
      // so EditClassDialog can be a full replica of AddClassForm.
      setEditingClass({
        ...cls,
        ...(details || {}),
        id: details?.id ?? classId ?? resolvedId,
        class_id: cls?.class_id ?? classId ?? details?.id,
        session_id: cls?.session_id ?? cls?.sessionId,
      });
    } catch (e) {
      console.error("Failed to open edit dialog", e);
      toast.error("Failed to load class for editing");
    } finally {
      setFetchingEditDetails(false);
    }
  };

  // Loading state
  // if (loading) {
  //   return (
  //     <EmptyState
  //       title={<Spinner />}
  //       description="Please wait while we load the classes."
  //     />
  //   );
  // }

  // Error state
  // if (error) {
  //   return (
  //     <EmptyState
  //       icon={BookOpenIcon}
  //       title="Error Loading Classes"
  //       description={error}
  //     />
  //   );
  // }

  // Empty state
  // if (!filteredClasses.length) {
  //   return (
  //     <EmptyState
  //       icon={BookOpenIcon}
  //       title="No Classes"
  //       description="No classes available at the moment."
  //     />
  //   );
  // }

  return (
    <>
      <div className="w-full mx-auto">
        <div className="flex items-center gap-4 mb-4 w-full">
          <div className="flex-1">
            <TableToolbar
              searchTerm={search}
              onSearchChange={setSearch}
              filters={[
                ...(Array.isArray(extraFilters) ? extraFilters : []),
                {
                  label: "Session Type",
                  value: sessionType,
                  options: sessionTypes,
                  onChange: setSessionType,
                  onClear: () => setSessionType("all"),
                },
              ]}
              sorts={effectiveSorts}
              dropdownPosition="after"
              // dropdowns={[
              //   {
              //     key: "timezone",
              //     label: "Timezone",
              //     buttonLabel: "Timezone",
              //     // icon: Globe,
              //     value: timezone,
              //     options: timezones,
              //     searchable: true,
              //     searchPlaceholder: "Search timezones...",
              //     onChange: setTimezone,
              //     onClear: () => setTimezone("Africa/Nairobi"),
              //   },
              // ]}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {loading ? (
            <EmptyState
              title={<Spinner />}
              description="Please wait while we load the classes."
            />
          ) : error ? (
            <EmptyState
              icon={BookOpenIcon}
              title="Error Loading Classes"
              description={error}
            />
          ) : !classes || classes.length === 0 ? (
            <EmptyState
              icon={BookOpenIcon}
              title="No Classes"
              description="You have not created any classes yet."
            />
          ) : sortedClasses.length === 0 ? (
            <EmptyState
              icon={BookOpenIcon}
              title="No Classes"
              description="No classes match your filters or search."
            />
          ) : (
            <>
              {sortedClasses.map((cls, idx) => {
                const canManage = canManageClass(cls);
                const start =
                  parseSessionDateTimeInSourceTimezone(
                    cls.session_date,
                    cls.session_start_time,
                  ) ||
                  new Date(`${cls.session_date}T${cls.session_start_time}`);
                const parsedEnd =
                  parseSessionDateTimeInSourceTimezone(
                    cls.session_date,
                    cls.session_end_time,
                  ) ||
                  (cls.session_end_time
                    ? new Date(`${cls.session_date}T${cls.session_end_time}`)
                    : null);
                const end =
                  parsedEnd && !Number.isNaN(parsedEnd.getTime())
                    ? parsedEnd
                    : new Date(
                        start.getTime() +
                          (cls.session_duration_minutes || 0) * 60000,
                      );

                return (
                  <ClassCard
                    key={idx}
                    startTime={start}
                    endTime={end}
                    title={cls.class_title}
                    instructor={`${cls.owner_first_name} ${cls.owner_last_name}`}
                    location={cls.class_location}
                    timeZone={timezone}
                    meetingLink={cls.session_meeting_link}
                    sessionType={cls.class_type}
                    room={cls.session_room}
                    sessionId={cls.session_id}
                    classId={cls.class_id ?? cls.classId ?? cls.id}
                    tmId={cls.tm_id ?? cls.tmId ?? cls.__tm_id}
                    recurrencePattern={
                      cls.recurrence_pattern ?? cls.recurrencePattern
                    }
                    canManageClass={canManage}
                    onEdit={() => handleEditClass(cls)}
                    onDelete={() => handleDeleteClass(cls)}
                    refetchClasses={fetchClasses}
                  />
                );
              })}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(num) => {
                  if (typeof onRowsPerPageChange === "function")
                    onRowsPerPageChange(num);
                }}
                onPageChange={(page) => {
                  if (typeof onPageChange === "function") onPageChange(page);
                }}
                totalRows={totalRows}
                rowLabel="classes"
              />
            </>
          )}
        </div>
      </div>
      {editingClass ? (
        <Suspense fallback={null}>
          <EditClassDialog
            open={!!editingClass}
            classData={editingClass}
            onClose={() => setEditingClass(null)}
            onClassUpdated={fetchClasses}
          />
        </Suspense>
      ) : null}
    </>
  );
};

export default ClassSchedule;
